import type { SupabaseClient } from "@supabase/supabase-js";

/**
 * Who a login is, asked of the link and never of the address (ENG-726).
 *
 * `auth.users` is the login. `nowadays.users` is the person. Until ENG-726 the
 * only thing joining them was the email string, so the app resolved a person
 * from the session's email claim. That claim is accurate but proves nothing:
 * Supabase "Confirm email" is off, so anybody may create a login on somebody
 * else's address, and the claim then honestly reports the victim's address.
 *
 * `nowadays.user_auth_identity` is the join that replaces it. A login that
 * appears in no row of it is nobody. One person may hold several logins (SSO
 * alongside Google, for example), so the link is many logins to one person.
 *
 * Mirrors `apps/backend/src/identity/supabase-identity.ts` in the backend. Safe
 * to import from browser code: a session client reads only its own link.
 */

/** The person a login is bound to. */
export interface LinkedIdentity {
  /** `auth.users.id`: the login. */
  authUserId: string;
  /** `nowadays.users.user_id`: the person. */
  userId: number;
  /**
   * The address stored on the person's account. Not the session's email claim,
   * though it usually reads the same. Compare stored addresses against this one.
   */
  email: string;
}

/**
 * The `link_source` of a link the signup trigger made before anybody proved the
 * mailbox. A password signup on an address nobody holds gets one, so it proves
 * nothing and must not identify anybody. The first proved sign-in cleans the
 * login and rewrites it to `confirmation` (see `confirmSignupLink`). Until then
 * the login is nobody, here and in the database's `current_auth_user_id()`.
 */
export const UNPROVED_LINK_SOURCE = "signup";

/** One link row, as {@link resolveLinkedIdentity} selects it. */
interface LinkRow {
  user_id: number;
  users: { email: string | null } | null;
}

/**
 * Supabase issues a UUID for every login, and the link is keyed by one. Checked
 * here so a malformed value resolves to nobody instead of a Postgres cast error.
 */
const SUPABASE_USER_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The login id to look up, or null when there is nothing usable.
 *
 * @param authUserId - The `id` of a verified Supabase user (the JWT `sub`).
 * @returns The trimmed id, or null when it is absent or not a UUID.
 */
export function linkableLoginId(authUserId: string | null | undefined): string | null {
  const id = authUserId?.trim();
  return id && SUPABASE_USER_ID.test(id) ? id : null;
}

/**
 * Reads a login's proved link, with whatever the caller embeds through `columns`.
 *
 * The one place the link is read to decide who somebody is, so the rules live
 * once: the id must be a Supabase login id, and a signup link nobody has proved
 * yet ({@link UNPROVED_LINK_SOURCE}) does not count.
 *
 * @param client - A service-role client, or a session client for the same login
 *   (RLS lets a login read only its own link).
 * @param authUserId - The `id` of a verified Supabase user. Never a value the
 *   caller supplied: the result grants access.
 * @param columns - The PostgREST select, for example `"user_id, users(email)"`.
 * @returns The row, or null when the id is unusable or the login has no proved link.
 * @throws When the lookup fails, so an outage is not mistaken for "nobody".
 */
export async function readProvedLink<Row>(
  client: SupabaseClient,
  authUserId: string | null | undefined,
  columns: string
): Promise<Row | null> {
  const loginId = linkableLoginId(authUserId);
  if (!loginId) return null;

  const { data, error } = await client
    .schema("nowadays")
    .from("user_auth_identity")
    .select(columns)
    .eq("supabase_user_id", loginId)
    .neq("link_source", UNPROVED_LINK_SOURCE)
    .maybeSingle<Row>();

  if (error) throw new Error(`Error reading the login's link: ${error.message}`);
  return data;
}

/**
 * The `nowadays.users` account a Supabase login is bound to.
 *
 * Returns null for a login bound to nothing, or bound only by an unproved
 * signup link ({@link UNPROVED_LINK_SOURCE}). That is the right answer for
 * somebody who has typed an address and proved nothing. Callers must treat null
 * as "no account" and never fall back to the address.
 *
 * @param client - A service-role client, or a session client for the same login
 *   (RLS lets a login read only its own link).
 * @param authUserId - The `id` of a verified Supabase user. Never a value the
 *   caller supplied: the result grants access.
 * @returns The linked identity, or null when the login is not linked.
 * @throws When the lookup itself fails, so an outage is not mistaken for "nobody".
 */
export async function resolveLinkedIdentity(
  client: SupabaseClient,
  authUserId: string | null | undefined
): Promise<LinkedIdentity | null> {
  const row = await readProvedLink<LinkRow>(client, authUserId, "user_id, users(email)");
  if (!row?.users?.email) return null;

  return { authUserId: linkableLoginId(authUserId)!, userId: row.user_id, email: row.users.email };
}

/** Browser-only memo for {@link resolveOwnLinkedIdentity}, keyed by login id. */
const ownIdentityMemo = new Map<string, Promise<LinkedIdentity | null>>();

/**
 * {@link resolveLinkedIdentity} for the signed-in browser, memoized per login for
 * the life of the page. Several components (and every wizard autosave) ask who
 * the current login is; this makes that one query instead of one per ask.
 *
 * Memoizes only in the browser, where the tab belongs to one person. On the
 * server a module-level memo would outlive the request and could hand one
 * caller's answer to another, so there it just resolves. Only a linked answer is
 * kept: "linked to nobody" and a failed lookup are forgotten, so the next ask
 * sees a login that was linked meanwhile (by an email code in this tab).
 *
 * @param client - The browser Supabase client for the same login.
 * @param authUserId - The `id` of the verified Supabase user.
 * @returns The linked identity, or null when the login is not linked.
 * @throws When the lookup fails.
 */
export function resolveOwnLinkedIdentity(
  client: SupabaseClient,
  authUserId: string | null | undefined
): Promise<LinkedIdentity | null> {
  const loginId = linkableLoginId(authUserId);
  if (!loginId) return Promise.resolve(null);
  if (typeof window === "undefined") return resolveLinkedIdentity(client, loginId);

  const cached = ownIdentityMemo.get(loginId);
  if (cached) return cached;

  const forget = () => ownIdentityMemo.delete(loginId);
  const pending = resolveLinkedIdentity(client, loginId).then(
    (identity) => {
      if (!identity) forget();
      return identity;
    },
    (error: unknown) => {
      forget();
      throw error;
    }
  );
  ownIdentityMemo.set(loginId, pending);
  return pending;
}

/**
 * The signed-in browser's own linked identity: reads the session, then
 * {@link resolveOwnLinkedIdentity}. The local session is enough to key the read,
 * because RLS checks the token on the link itself.
 *
 * @param client - The browser Supabase client.
 * @returns The linked identity, or null when signed out or linked to nobody.
 * @throws When the link lookup fails.
 */
export async function getOwnLinkedIdentity(client: SupabaseClient): Promise<LinkedIdentity | null> {
  const {
    data: { session },
  } = await client.auth.getSession();
  return session ? resolveOwnLinkedIdentity(client, session.user.id) : null;
}
